import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { goodsRecognitions, type GoodsRecognitionListItem } from '@/api/goodsWriteoffs';
import { refs } from '@/api/refs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsRecognitionsPage() {
  const orgNames = useOrgNames();
  const navigate = useNavigate();
  const f = useDocListFilters();
  const whQ = useQuery({ queryKey: ['warehouses-all'], queryFn: () => refs.warehouses() });

  // 2026-08-11 (Норма #29, субменю Склад): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<GoodsRecognitionListItem>(
    (limit, offset, sort) => goodsRecognitions.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.goods_recognitions' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<GoodsRecognitionListItem>('doc.goods_recognitions', f.orgId);

  const whName = useMemo(() => {
    const m = new Map<string, string>();
    whQ.data?.forEach((w) => m.set(w.id, w.name));
    return m;
  }, [whQ.data]);

  const cols = useMemo<ColDef<GoodsRecognitionListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: GoodsRecognitionListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'warehouse_id', headerName: 'Склад', flex: 1, minWidth: 200, sortable: false,
      valueFormatter: (p) => p.value ? (whName.get(p.value) ?? '') : '',
    },
    {
      field: 'total_amount', headerName: 'Сума', width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [whName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Оприбуткування лишків<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/goods-recognitions/new')}>
          + Нове оприбуткування
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <InfiniteDocGrid<GoodsRecognitionListItem>
        copyRow={(r) => navigate(`/goods-recognitions/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => goodsRecognitions.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="goods-recognitions"
        onRowDoubleClicked={(r) => navigate(`/goods-recognitions/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
